import React, { useEffect, FC, useState, useRef, useCallback } from "react";
import { Form } from "antd";
import axios from "axios";
import SearchSelected from "components/foundation/selected/SearchSelected";

interface AddressModalProps {
  wardCode: string;
  districtCode: string;
  provinceCode: string;
  provinceTitle: string;
  districtTitle: string;
  wardTitle: string;
  provinceField: string;
  districtField: string;
  wardField: string;
  setProvinceCode: (code: string) => void;
  setDistrictCode: (code: string) => void;
  setWardCode: (code: string) => void;
}

const AddressModal: FC<AddressModalProps> = ({
  wardCode,
  districtCode,
  provinceCode,
  provinceTitle,
  districtTitle,
  wardTitle,
  wardField,
  districtField,
  provinceField,
  setProvinceCode,
  setDistrictCode,
  setWardCode,
}) => {
  const form = Form.useFormInstance();

  const [provinces, setProvinces] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [wards, setWards] = useState<any[]>([]);

  const fetchProvinces = async () => {
    try {
      const response = await axios.get(
        "https://provinces.open-api.vn/api/v1/?depth=1",
      );
      setProvinces(response.data);
    } catch (error) {
      console.error("Error provinces:", error);
    }
  };

  const fetchDistricts = useCallback(async (pCode: string) => {
    if (!pCode) return;
    try {
      const response = await axios.get(
        `https://provinces.open-api.vn/api/v1/p/${pCode}?depth=2`,
      );
      setDistricts(response.data?.districts || []);
    } catch (error) {
      console.error("Error districts:", error);
    }
  }, []);

  const fetchWards = useCallback(async (dCode: string) => {
    if (!dCode) return;
    try {
      const response = await axios.get(
        `https://provinces.open-api.vn/api/v1/d/${dCode}?depth=2`,
      );
      setWards(response.data?.wards || []);
    } catch (error) {
      console.error("Error wards:", error);
    }
  }, []);

  useEffect(() => {
    fetchProvinces();
  }, []);

  useEffect(() => {
    if (provinceCode) fetchDistricts(provinceCode);
  }, [provinceCode, fetchDistricts]);

  useEffect(() => {
    if (districtCode) fetchWards(districtCode);
  }, [districtCode, fetchWards]);

  const handleProvinceChange = (value: string) => {
    setProvinceCode(value);
    setDistrictCode("");
    setWardCode("");
    setDistricts([]);
    setWards([]);

    form.setFieldsValue({
      [provinceField]: value,
      [districtField]: undefined,
      [wardField]: undefined,
    });
  };

  const handleDistrictChange = (value: string) => {
    setDistrictCode(value);
    setWardCode("");
    setWards([]);

    form.setFieldsValue({
      [districtField]: value,
      [wardField]: undefined,
    });
  };

  const handleWardChange = (value: string) => {
    setWardCode(value);
    form.setFieldsValue({ [wardField]: value });
  };

  return (
    <div>
      <SearchSelected
        className="!mb-[18px]"
        title={provinceTitle}
        id={provinceField}
        bordered={false}
        options={provinces.map((p) => ({
          label: p.name,
          value: p.code.toString(),
        }))}
        onChange={handleProvinceChange}
        form={form}
      />

      <SearchSelected
        className="!mb-[18px]"
        title={districtTitle}
        id={districtField}
        bordered={false}
        options={districts.map((d) => ({
          label: d.name,
          value: d.code.toString(),
        }))}
        onChange={handleDistrictChange}
        form={form}
        disabled={!provinceCode}
      />

      <SearchSelected
        className="!mb-[18px]"
        title={wardTitle}
        id={wardField}
        bordered={false}
        options={wards.map((w) => ({
          label: w.name,
          value: w.code.toString(),
        }))}
        onChange={handleWardChange}
        form={form}
        disabled={!districtCode}
      />
    </div>
  );
};

export default AddressModal;
